/**
 * Municipality and curated landing pages — the shared selectors (Phase 6).
 *
 * Both the React pages and the prerender/sitemap scripts import THIS module,
 * so a landing can never list different beaches in the app and in the
 * generated HTML (the plan's "shared selectors, not duplicated arrays").
 * CommonJS for the same reason as ./beachUrls.js.
 *
 * "Playas para familias" is deliberately ABSENT: the catalog has no
 * family-suitability field, and deriving one from other attributes would be
 * an undocumented editorial judgement (audit §3). It can join when the
 * catalog gains a real field.
 *
 * All predicates read STATIC catalog data only. No landing claims a live
 * condition; the live data belongs to each beach's detail page.
 */

/* eslint-env node, commonjs */
'use strict';

// eslint-disable-next-line @typescript-eslint/no-var-requires -- CJS on purpose (see header)
const { slugify, beachPath } = require('./beachUrls');

/** Webcam exists and is not editorially disabled (mirror of webcamDisponible). */
function withWebcam(p) {
  return Boolean(p.webcam) && p.webcam.estado !== 'desactivada';
}

/** Explicitly marked accessible. Absent attribute = unknown, NOT false → excluded. */
function isAccessible(p) {
  return p.atributos?.accesible === true;
}

/**
 * A lifeguard post is registered for the beach. Catalog-level mirror of the
 * flagAggregation convention: a station with id > 0, or idCruzRoja > 0
 * ("0 = no coverage").
 */
function withLifeguard(p) {
  if ((p.cruzRojaStations ?? []).some((e) => typeof e.id === 'number' && e.id > 0)) return true;
  return (p.idCruzRoja ?? 0) > 0;
}

/** Explicitly marked for surf. */
function forSurf(p) {
  return p.atributos?.surf === true;
}

/**
 * The curated landings. `textos` is the Spanish single source: i18n/es.ts
 * imports it for the `landing.*` keys and the prerender script bakes it.
 */
const LANDINGS = [
  {
    id: 'playas-con-webcam',
    filtro: withWebcam,
    textos: {
      titulo: 'Playas con webcam en {region}',
      intro:
        'Playas de {region} con webcam pública para ver la zona antes de ir. El enlace abre el servicio externo de cada cámara; la app no comprueba si emite.',
    },
  },
  {
    id: 'playas-accesibles',
    filtro: isAccessible,
    textos: {
      titulo: 'Playas accesibles en {region}',
      intro:
        'Playas del catálogo de {region} marcadas como accesibles. Es información fija de la playa: los servicios pueden ser estacionales.',
    },
  },
  {
    id: 'playas-con-socorrista',
    filtro: withLifeguard,
    textos: {
      titulo: 'Playas con socorrismo en {region}',
      intro:
        'Playas de {region} con puesto de socorrismo registrado. La bandera y el horario de hoy se cargan en la ficha de cada playa.',
    },
  },
  {
    id: 'playas-para-surf',
    filtro: forSurf,
    textos: {
      titulo: 'Playas para surf en {region}',
      intro:
        'Playas del catálogo de {region} señaladas para surf. El estado del mar de hoy se consulta en la ficha de cada playa.',
    },
  },
  {
    id: 'playas-con-perros',
    filtro: (p) => p.atributos?.mascotas === true,
    textos: {
      titulo: 'Playas para perros en {region}',
      intro:
        'Playas del catálogo de {region} marcadas como aptas para mascotas. Las normas y horarios pueden cambiar según la temporada: confírmalos con el ayuntamiento antes de ir.',
    },
  },
  {
    id: 'playas-nudistas',
    filtro: (p) => p.atributos?.nudista === true,
    textos: {
      titulo: 'Playas nudistas en {region}',
      intro: 'Playas del catálogo de {region} marcadas como nudistas.',
    },
  },
];

/** Landings with at least one beach: empty categories are never published. */
function nonEmptyLandings(beaches) {
  return LANDINGS.filter((l) => beaches.some(l.filtro));
}

/** Unique municipality names, alphabetical. */
function municipalitiesOf(beaches) {
  return Array.from(new Set(beaches.map((p) => p.municipio))).sort((a, b) => a.localeCompare(b));
}

function municipalityPath(municipality) {
  return `/municipios/${slugify(municipality)}`;
}

/**
 * One row per municipality: name, canonical route and beach count. The
 * /municipios index (app page and prerendered file) is built from THIS,
 * so both always agree.
 */
function municipalitiesSummary(beaches) {
  return municipalitiesOf(beaches).map((municipality) => ({
    municipio: municipality,
    ruta: municipalityPath(municipality),
    total: beaches.filter((p) => p.municipio === municipality).length,
  }));
}

/** Beaches of the municipality a slug points at (empty array if unknown). */
function beachesOfMunicipalitySlug(beaches, municipalitySlug) {
  return beaches.filter((p) => slugify(p.municipio) === municipalitySlug);
}

module.exports = {
  LANDINGS,
  nonEmptyLandings,
  municipalitiesOf,
  municipalityPath,
  municipalitiesSummary,
  beachesOfMunicipalitySlug,
  beachPath,
};
